<template>
    <div id="membership-details"
         class="container container--top-padding-for-mobile-header">
        <div class="mobile-header">
            <h2 v-if="currentMembership">
                {{ l('MEMBERSHIP_DETAILS.MEMBERSHIP') }}
                <strong>{{ currentMembership.ref }}</strong>
            </h2>
        </div>

        <!-- Loading spinner while loading membership -->
        <PageLoadingSpinner v-if="loading"
                            color="light"
                            lang="MEMBERSHIP_DETAILS.LOADING" />

        <!-- Membership not found message -->
        <NoResults v-else-if="notFound"
                   icon="fa-solid fa-times-circle"
                   color="light"
                   lang="MEMBERSHIP_DETAILS.NOT_FOUND">
            <!-- Back button for not found page -->
            <BackBtn class="mt-1" />
        </NoResults>

        <!-- Membership details -->
        <div v-else-if="currentMembership"
             class="two-col-page two-col-page--flip-desktop invert">
            <!-- Right-hand column with tabs -->
            <div class="two-col-page__content">
                <div id="membership-details-header">
                    <Tabs :model-value="activeTabName"
                          :tabs="tabs"
                          @update:model-value="selectTab" />
                </div>

                <div class="tab-container invert">
                    <MembershipDetailsMembersTab v-if="activeTabName === 'members'"
                                                 :membership="currentMembership"
                                                 :membership-subscription="currentMembershipSubscription"
                                                 :selected-member="selectedMember"
                                                 @edit-member="editMember"
                                                 @toggle-visit="toggleMemberVisit"
                                                 @log-visit="logVisit"
                                                 @unlog-visit="unlogVisit" />

                    <MembershipDetailsVisitsTab v-else-if="activeTabName === 'visits'"
                                                :membership="currentMembership" />

                    <MembershipDetailsPaymentsTab v-else-if="activeTabName === 'payments'"
                                                  :membership="currentMembership" />

                    <MembershipDetailsLinkedMembersTab v-else-if="activeTabName === 'linked-members'"
                                                       :membership="currentMembership"
                                                       @toggle-visit="toggleMemberVisit" />
                </div>
            </div>

            <!-- Left column with buttons and details -->
            <div class="two-col-page__sidebar">
                <div class="page-action-btns desktop-only">
                    <BackBtn />
                </div>

                <MembershipDetailsDetailsCol :membership="currentMembership"
                                             :membership-subscription="currentMembershipSubscription"
                                             :stop-renewing-in-progress="stopRenewingInProgress"
                                             @amend-members="amendMembers(currentMembership, null)"
                                             @cancel-membership="cancelMembership(currentMembership)"
                                             @change-plan="changeMembershipPlan(currentMembership)"
                                             @stop-renewing="stopRenewingMembership(currentMembership)" />
            </div>
        </div>
    </div>
</template>

<script>
import BackBtn from '@/vue/components/BackBtn';
import CurrentRedemptionAction from '@/lib/Memberships/CurrentRedemptionAction';
import EventNames from '@/lib/Events/EventNames';
import MembershipDetailsDetailsCol from '@/epos/vue/pages/membership-details/components/MembershipDetailsDetailsCol';
import MembershipDetailsLinkedMembersTab from '@/epos/vue/pages/membership-details/tabs/MembershipDetailsLinkedMembersTab';
import MembershipDetailsMembersTab from '@/epos/vue/pages/membership-details/tabs/MembershipDetailsMembersTab';
import MembershipDetailsPaymentsTab from '@/epos/vue/pages/membership-details/tabs/MembershipDetailsPaymentsTab';
import MembershipDetailsVisitsTab from '@/epos/vue/pages/membership-details/tabs/MembershipDetailsVisitsTab';
import NoResults from '@/vue/components/NoResults';
import PageLoadingSpinner from '@/vue/components/PageLoadingSpinner';
import PrivilegeNames from '@/lib/Privileges/PrivilegeNames';
import Tabs from '@/vue/components/Tabs/Tabs';
import { ANGULAR_MODALS } from '@/lib/Modals/ANGULAR_MODALS';
import { container } from '@/container';
import { invalidateOrderSearchCache } from '@/lib/Orders/orderSearchCacheFuncs';
import { mapState } from 'vuex';

export default {
    components: {
        BackBtn,
        MembershipDetailsDetailsCol,
        MembershipDetailsLinkedMembersTab,
        MembershipDetailsMembersTab,
        MembershipDetailsPaymentsTab,
        MembershipDetailsVisitsTab,
        NoResults,
        PageLoadingSpinner,
        Tabs,
    },

    data() {
        return {
            autoRedeemProfile: container.userService.company.getAutoRedeemProfile(),
            initialSelectedMemberRef: null,
            lastScannedMemberRef: container.redemptionScanHandler.pullLastScannedMemberRef(),
            loading: false,
            notFound: false,
        };
    },

    computed: {
        ...mapState('membershipDetails', [
            'currentMembership',
            'currentMembershipSubscription',
            'selectedMember',
            'activeTabName',
            'stopRenewingInProgress',
        ]),

        tabs() {
            const tabs = [
                {
                    name: 'members',
                    label: 'MEMBERSHIP_DETAILS.MEMBER_DETAILS_TAB',
                    icon: 'fa-solid fa-users',
                },
                {
                    name: 'visits',
                    label: 'MEMBERSHIP_DETAILS.VISIT_HISTORY_TAB',
                    icon: 'fa-solid fa-clock-rotate-left',
                },
                {
                    name: 'payments',
                    label: 'MEMBERSHIP_DETAILS.VIEW_PAYMENTS_TAB',
                    icon: 'fa-solid fa-coins',
                },
            ];

            if (this.currentMembership && this.currentMembership.customerAccount) {
                tabs.push({
                    name: 'linked-members',
                    label: 'MEMBERSHIP_DETAILS.LINKED_MEMBERS_TAB',
                    icon: 'fa-solid fa-link-horizontal',
                });
            }

            return tabs;
        },

        membershipRefFromPath() {
            const path = this.$route.path;
            const parts = path.split('/');
            // Path is /memberships/:membershipRef or /memberships/:membershipRef/:memberRef
            return parts[2];
        },

        memberRefFromPath() {
            const path = this.$route.path;
            const parts = path.split('/');
            // Path is /memberships/:membershipRef/:memberRef
            return parts[3] || null;
        },

        fromBarcodeScan() {
            return !!(this.lastScannedMemberRef
                && this.initialSelectedMemberRef
                && this.lastScannedMemberRef === this.initialSelectedMemberRef);
        },
    },

    watch: {
        membershipRefFromPath: {
            handler(membershipRef) {
                if (membershipRef) {
                    this.initialSelectedMemberRef = this.memberRefFromPath;
                    this.loadMembership(membershipRef, true);
                }
            },
            immediate: true,
        },
    },

    mounted() {
        container.membershipService.showRenewalSuccessIfAppropriate();

        // Set up barcode scanning listener
        this.deregisterBarcodeScannedListener = container.$rootScope.$on(
            EventNames.BARCODE_SCANNED,
            (event, response) => {
                if (response.hasOpenModal) {
                    return;
                }

                const barcode = response.code.toUpperCase();
                if (barcode.length > 0 && this.currentMembership) {
                    // Check if this barcode belongs to one of the members on the screen
                    const member = this.currentMembership.getMemberByRef(barcode);
                    if (member) {
                        this.selectMember(member, true);
                    } else if (this.currentMembership && barcode === this.currentMembership.ref.toUpperCase()) {
                        // The scanned barcode was the ref for the membership that is currently displayed
                        this.selectMember(null, false);
                    } else {
                        // Otherwise run a search for this barcode
                        container.navigationService.viewRedeemWithScannedBarcode(response.code);
                    }
                }
            }
        );

        // Switch to the Members tab by default.
        this.selectTab('members');
    },

    unmounted() {
        // Clean up barcode listener
        if (this.deregisterBarcodeScannedListener) {
            this.deregisterBarcodeScannedListener();
        }
    },

    methods: {
        selectTab(tabName) {
            this.$store.commit('membershipDetails/setActiveTabName', tabName);
        },

        async loadMembership(membershipRef, showLoading) {
            if (showLoading !== false) {
                this.loading = true;
                this.notFound = false;
            }

            try {
                const membership = await container.membershipService.getMembershipByRef(membershipRef);
                this.loading = false;
                this.membershipLoaded(membership);
            } catch (error) {
                this.loading = false;
                this.notFound = true;
            }
        },

        membershipLoaded(membership) {
            this.$store.commit('membershipDetails/setCurrentMembership', membership);
            this.$store.commit('membershipDetails/setCurrentMembershipSubscription', membership.getCurrentSubscription());

            // Determine the selected member
            let member;
            if (this.selectedMember) {
                // Replace the selectedMember when reloading
                member = this.getMemberById(this.selectedMember.ID);
                if (member) {
                    this.selectMember(member, false);
                }
            } else if (this.initialSelectedMemberRef) {
                member = membership.getMemberByRef(this.initialSelectedMemberRef);
                if (member) {
                    this.selectMember(member, this.fromBarcodeScan);
                }
            } else {
                this.selectMember(null, false);
            }
        },

        getMemberById(id) {
            if (!this.currentMembership) {
                return null;
            }
            for (let i = 0; i < this.currentMembership.members.length; i++) {
                const member = this.currentMembership.members[i];
                if (member.ID === id) {
                    return member;
                }
            }
            return null;
        },

        reloadMembership() {
            if (!this.currentMembership) {
                return;
            }
            this.loadMembership(this.currentMembership.ref, false);
        },

        getCurrentMembershipUrl() {
            if (!this.currentMembership) {
                return null;
            }
            return '/memberships/' + this.currentMembership.getRef();
        },

        cancelMembership(membership) {
            container.membershipManagementService.cancelMemberships(
                [membership],
                this.getCurrentMembershipUrl(),
                membership.customerAccount
            );
        },

        changeMembershipPlan(membership) {
            container.membershipManagementService.changeMembershipPlans(
                [membership],
                this.getCurrentMembershipUrl(),
                membership.customerAccount
            );
        },

        stopRenewingMembership(membership) {
            this.$store.commit('membershipDetails/setStopRenewingInProgress', true);
            container.membershipManagementService.stopRenewingMemberships(
                [membership],
                () => {
                    this.$store.commit('membershipDetails/setStopRenewingInProgress', false);
                }
            );
        },

        logVisit() {
            const membership = this.currentMembership;
            const membershipSubscription = this.currentMembershipSubscription;
            const membershipPlan = membershipSubscription.membershipPlan;

            if (membership.currentRedemptionAction !== CurrentRedemptionAction.NONE) {
                console.log('MembershipDetailsView.logVisit() return early.');
                return;
            }
            membership.currentRedemptionAction = CurrentRedemptionAction.REDEEMING;
            this.clearSelectedMember();
            container.membershipRedemptionService.logVisit(
                membership,
                membershipSubscription,
                membershipPlan,
                () => {
                    this.reloadMembership();
                }
            );
        },

        unlogVisit() {
            const membership = this.currentMembership;
            const membershipSubscription = this.currentMembershipSubscription;
            const membershipPlan = membershipSubscription.membershipPlan;

            if (membership.currentRedemptionAction !== CurrentRedemptionAction.NONE) {
                console.log('MembershipDetailsView.unlogVisit() return early.');
                return;
            }
            membership.currentRedemptionAction = CurrentRedemptionAction.UNREDEEMING;
            container.privilegesManager
                .requirePrivilege(PrivilegeNames.ALLOW_UNREDEEM_MEMBERSHIPS)
                .then(() => {
                    this.clearSelectedMember();
                    container.membershipRedemptionService.unlogVisit(
                        membership,
                        membershipSubscription,
                        membershipPlan,
                        () => {
                            this.reloadMembership();
                        }
                    );
                }).catch(() => {
                    membership.currentRedemptionAction = CurrentRedemptionAction.NONE;
                });
        },

        clearSelectedMember() {
            this.$store.commit('membershipDetails/setSelectedMember', null);
            this.initialSelectedMemberRef = null;
            this.lastScannedMemberRef = null;
        },

        toggleMemberVisit(member, membershipSubscription, membershipPlan, membership) {
            if (
                member.currentRedemptionAction !== CurrentRedemptionAction.NONE
                || membership.currentRedemptionAction !== CurrentRedemptionAction.NONE) {
                return;
            }

            this.clearSelectedMember();

            if (member.hasVisitedToday()) {
                member.currentRedemptionAction = CurrentRedemptionAction.UNREDEEMING;
                this.unlogMemberVisit(member, membershipSubscription, membershipPlan, membership);
            } else {
                member.currentRedemptionAction = CurrentRedemptionAction.REDEEMING;
                this.logMemberVisit(member, membershipSubscription, membershipPlan, membership);
            }
        },

        memberCanBeRedeemed(member, membershipSubscription, membershipPlan, membership) {
            return member.hasContact()
                && !member.hasVisitedToday()
                && membership.canBeRedeemedRightNow()
                && membershipPlan.membershipsCanBeRedeemedToday;
        },

        logMemberVisit(member, membershipSubscription, membershipPlan, membership, autoRedeem) {
            if (member.currentRedemptionAction !== CurrentRedemptionAction.REDEEMING) {
                console.debug("Cancelling logMemberVisit because member's currentRedemptionAction is not REDEEMING.", member);
                return;
            }

            if (!this.memberCanBeRedeemed(member, membershipSubscription, membershipPlan, membership)) {
                console.debug('Cancelling logMemberVisit because member is not redeemable.', member);
                return;
            }

            const redeemQty = 1;
            container.membershipRedemptionService.updateMemberVisits(
                member,
                membershipSubscription,
                membershipPlan,
                redeemQty,
                () => {
                    this.reloadMembership();
                    this.postSingleMemberRedemption(membership, member, redeemQty, autoRedeem);
                }
            );

            invalidateOrderSearchCache();
        },

        memberCanBeUnredeemed(member, membershipSubscription, membershipPlan, membership) {
            return member.hasContact()
                && member.hasVisitedToday()
                && membership.canBeRedeemedRightNow()
                && membershipPlan.membershipsCanBeRedeemedToday;
        },

        unlogMemberVisit(member, membershipSubscription, membershipPlan, membership) {
            if (member.currentRedemptionAction !== CurrentRedemptionAction.UNREDEEMING) {
                console.debug("Cancelling logMemberVisit because member's currentRedemptionAction is not UNREDEEMING.", member);
                return;
            }

            if (!this.memberCanBeUnredeemed(member, membershipSubscription, membershipPlan, membership)) {
                console.debug('Cancelling logMemberVisit because member is not unredeemable.', member);
                return;
            }

            container.privilegesManager
                .requirePrivilege(PrivilegeNames.ALLOW_UNREDEEM_MEMBERSHIPS)
                .then(() => {
                    const redeemQty = -1;
                    container.membershipRedemptionService.updateMemberVisits(
                        member,
                        membershipSubscription,
                        membershipPlan,
                        redeemQty,
                        () => {
                            this.reloadMembership();
                            this.postSingleMemberRedemption(membership, member, redeemQty, false);
                        }
                    );

                    invalidateOrderSearchCache();
                });
        },

        postSingleMemberRedemption(membership, member, qty, autoRedeem) {
            const name = member.contact.getFullName();
            const messageKey = qty > 0 ? 'MEMBERSHIP_DETAILS.MEMBER_REDEEMED_MSG' : 'MEMBERSHIP_DETAILS.MEMBER_UNREDEEMED_MSG';
            const message = container.langService.getText(messageKey, { name });

            if (autoRedeem && qty > 0) {
                container.toastFactory.success('MEMBERSHIP_DETAILS.MEMBER_REDEEMED_AUTO', message);
            } else {
                if (qty > 0) {
                    container.toastFactory.success('MEMBERSHIP_DETAILS.MEMBER_REDEEMED', message);
                } else {
                    container.toastFactory.warning('MEMBERSHIP_DETAILS.MEMBER_UNREDEEMED', message);
                }

                // Redirect back to the redeem screen if the user arrived from there
                const searchParams = new URLSearchParams(window.location.hash.split('?')[1]);
                if (
                    qty > 0
                    && searchParams.has('fromRedeem')
                    && membership.members.length === 1
                ) {
                    container.navigationService.viewRedeem();
                }
            }
        },

        editMember(member) {
            this.amendMembers(this.currentMembership, member.ID);
        },

        amendMembers(membership, currentMemberID) {
            const membershipDataset = membership.toMembershipDataset();

            const modalInstance = container.modalFactory.openFromData(
                ANGULAR_MODALS.MEMBERSHIP_DATASETS,
                {
                    action: () => 'edit-existing',
                    membershipPlan: () => membership.currentPlan,
                    currentMemberID: () => currentMemberID,
                    customerAccount: () => membership.customerAccount,
                    increaseQuantityBy: () => 0,
                    membershipDatasets: () => [membershipDataset],
                }
            );

            modalInstance.then(
                (data) => {
                    const dataset = data.membershipDatasets[0];

                    container.membershipService.updateMembership(
                        membership,
                        dataset,
                        (updatedMembership) => {
                            this.membershipLoaded(updatedMembership);
                        }
                    );
                },
                () => {
                    // Modal dismissed
                }
            );
        },

        selectMember(member, autoRedeem) {
            this.$store.commit('membershipDetails/setSelectedMember', member);

            if (autoRedeem && member && this.autoRedeemProfile && this.autoRedeemProfile.redeemMembersWhenScanned) {
                const canRedeemMember = this.memberCanBeRedeemed(
                    member,
                    this.currentMembershipSubscription,
                    this.currentMembership.currentPlan,
                    this.currentMembership
                );
                if (canRedeemMember) {
                    member.currentRedemptionAction = CurrentRedemptionAction.REDEEMING;
                    this.logMemberVisit(
                        member,
                        this.currentMembershipSubscription,
                        this.currentMembership.currentPlan,
                        this.currentMembership,
                        autoRedeem
                    );
                }
            }
        },

        handleBack() {
            container.navigationService.goBack();
        },
    },
};
</script>
